// The DOM half of the online overlay: `OverlayView` in, a focus-trapped
// `@bn-games/ui-shell` modal out — reconnecting, connection lost, the rematch
// handshake and the result.
import {
  createGameStateOverlay,
  type GameStateOverlayButtonId,
  type GameStateOverlayHandle,
} from '@bn-games/ui-shell';
import type { SeatedPlayer } from '@bn-games/network-protocol';
import { t } from '../i18n/I18n.js';
import type { OverlayView } from '../network/overlayState.js';
import type { GameOverView } from '../scenes/GameOverHost.js';
import { buildOnlineOverlayLabels } from './onlineOverlayLabels.js';

export interface OnlineOverlayDeps {
  /** The finished game, worded from our side; null while it is still on. */
  readonly result: () => GameOverView | null;
  readonly opponent: () => SeatedPlayer | null;
  /** PGN/FEN export for the finished game; the same node until the next game. */
  readonly exportRow: () => HTMLElement | null;
  readonly requestRematch: () => void;
  readonly acceptRematch: () => void;
  readonly declineRematch: () => void;
  /** Dismiss a state the player may simply walk away from. */
  readonly dismiss: () => void;
  /** Leave the table — back to the menu. */
  readonly leave: () => void;
}

export interface OnlineOverlayHost {
  update(view: OverlayView): void;
  dispose(): void;
}

export function createOnlineOverlayHost(deps: OnlineOverlayDeps): OnlineOverlayHost {
  let handle: GameStateOverlayHandle | null = null;

  function onAction(id: GameStateOverlayButtonId): void {
    switch (id) {
      case 'rematch':
        deps.requestRematch();
        return;
      case 'accept':
        deps.acceptRematch();
        return;
      case 'decline':
        deps.declineRematch();
        return;
      case 'cancel':
        deps.dismiss();
        return;
      case 'leave':
      case 'return-to-menu':
        deps.leave();
        return;
    }
  }

  function close(): void {
    handle?.close();
    handle = null;
  }

  return {
    update: (view) => {
      if (view.overlay === 'none') {
        close();
        return;
      }
      // Labels as a getter: re-resolved on every update, so a language change
      // or a new result lands without rebuilding the handle.
      handle ??= createGameStateOverlay(() => buildOnlineOverlayLabels(deps.result()), { onAction });
      const opponent = deps.opponent();
      handle.update({
        state: view.overlay,
        opponentName: opponent?.name ?? t('common.opponent'),
        opponentAvatarUrl: opponent?.avatarUrl ?? null,
        opponentUserId: opponent?.userId ?? null,
        gracePeriodEndsAt: view.gracePeriodEndsAt,
        extraContent: deps.exportRow(),
        ...(view.gameOverResult !== null ? { gameOverResult: view.gameOverResult } : {}),
      });
    },
    dispose: close,
  };
}
